// 管理后台认证状态管理Hook - 独立命名空间版本
import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserInfo,
  adminLogout as logoutApi,
  getCurrentAdminUser,
} from '@/services/auth';
import { OFFER_CREATION_DRAFT_ID_STORAGE_KEY } from '@/features/offer/components/offer-creation-draft-utils';

// 认证状态枚举
enum AdminAuthStatus {
  LOADING = 'loading',
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated',
}

// 管理后台认证上下文类型
interface AdminAuthContextType {
  adminUser: UserInfo | null;
  isAdminAuthenticated: boolean;
  isAdminLoading: boolean;
  adminAuthStatus: AdminAuthStatus;
  adminLogin: (user: UserInfo) => void;
  adminLogout: () => Promise<void>;
  updateAdminUser: (user: Partial<UserInfo>) => void;
  refreshAdminAuth: () => Promise<void>;
}

// 创建管理后台认证上下文
const AdminAuthContext = createContext<AdminAuthContextType | undefined>(
  undefined
);

// 管理后台认证提供者组件
interface AdminAuthProviderProps {
  children: ReactNode;
}

export function AdminAuthProvider({ children }: AdminAuthProviderProps) {
  const [adminUser, setAdminUser] = useState<UserInfo | null>(null);
  const [adminAuthStatus, setAdminAuthStatus] = useState<AdminAuthStatus>(
    AdminAuthStatus.LOADING
  );
  const navigate = useNavigate();

  const isAdminLoading = adminAuthStatus === AdminAuthStatus.LOADING;
  const isAdminAuthenticated =
    adminAuthStatus === AdminAuthStatus.AUTHENTICATED;

  const clearOfferCreationDraftSession = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(OFFER_CREATION_DRAFT_ID_STORAGE_KEY);
  }, []);

  // 刷新管理后台认证状态 - 使用专门的管理员API
  const refreshAdminAuth = useCallback(async () => {
    console.log('🔐 刷新管理后台认证状态...');

    try {
      const fetchedUser = await getCurrentAdminUser();
      console.log('🔐 管理员Cookie认证验证成功，获取用户信息:', fetchedUser);
      setAdminUser(fetchedUser);
      setAdminAuthStatus(AdminAuthStatus.AUTHENTICATED);
    } catch (error) {
      console.log('🔐 管理员Cookie认证验证失败，设置为未认证状态:', error);
      setAdminUser(null);
      setAdminAuthStatus(AdminAuthStatus.UNAUTHENTICATED);
    }
  }, []);

  // 初始化管理后台认证状态
  useEffect(() => {
    let isMounted = true;

    const initAdminAuth = async () => {
      console.log('🔐 开始初始化管理后台认证状态...');

      // 如果当前在OAuth回调页面，跳过初始化认证检查，等待OAuth流程完成
      if (window.location.pathname === '/oauth/callback') {
        console.log('🔐 当前在OAuth回调页面，跳过初始化认证检查');
        return;
      }

      await refreshAdminAuth();

      if (isMounted) {
        console.log('🔐 管理后台认证状态初始化完成');
      }
    };

    void initAdminAuth();

    return () => {
      isMounted = false;
    };
  }, [refreshAdminAuth]);

  // 管理员登录
  const adminLogin = (userInfo: UserInfo) => {
    console.log('🔐 adminLogin 被调用，管理员信息:', userInfo);

    setAdminUser(userInfo);
    setAdminAuthStatus(AdminAuthStatus.AUTHENTICATED);

    // 强制更新状态后立即检查
    setTimeout(() => {
      console.log('🔐 管理员登录后状态检查:', {
        user: userInfo,
        adminAuthStatus: AdminAuthStatus.AUTHENTICATED,
        isAdminAuthenticated: true,
      });
    }, 0);
  };

  // 管理员退出登录 - 与前台保持一致
  const adminLogout = useCallback(async () => {
    try {
      console.log('🔐 执行管理员登出操作...');
      await logoutApi(); // 调用后端登出接口，清除服务器端 session/cookie
    } catch (error) {
      console.error('🔐 管理员退出登录失败:', error);
    } finally {
      // 清除本地状态
      setAdminUser(null);
      setAdminAuthStatus(AdminAuthStatus.UNAUTHENTICATED);
      clearOfferCreationDraftSession();

      // 管理后台退出后总是跳转到管理后台登录页
      console.log('🔐 管理员登出，跳转到管理后台登录页');
      navigate('/admin/login', { replace: true });
    }
  }, [clearOfferCreationDraftSession, navigate]);

  // 更新管理员用户信息
  const updateAdminUser = (updatedUser: Partial<UserInfo>) => {
    if (adminUser) {
      const newUser = { ...adminUser, ...updatedUser };
      setAdminUser(newUser);
      console.log('🔐 管理员信息已更新:', newUser);
    }
  };

  const value: AdminAuthContextType = {
    adminUser,
    isAdminAuthenticated,
    isAdminLoading,
    adminAuthStatus,
    adminLogin,
    adminLogout,
    updateAdminUser,
    refreshAdminAuth,
  };

  return React.createElement(AdminAuthContext.Provider, { value }, children);
}

// 使用管理后台认证Hook
export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}

// 管理后台路由守卫Hook
export function useAdminAuthGuard() {
  const { isAdminAuthenticated, isAdminLoading } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdminLoading && !isAdminAuthenticated) {
      console.log('🔐 管理员未认证，跳转到管理后台登录页');
      navigate('/admin/login', { replace: true });
    }
  }, [isAdminAuthenticated, isAdminLoading, navigate]);

  return { isAdminAuthenticated, isAdminLoading };
}
